import React, { useState, useEffect } from 'react';
import PersonalityQuestionnaire from '../PersonalityQuestionnaire/PersonalityQuestionnaire';
import styles from './PreferencesModal.module.css';
import { preferencesService } from '../../../services/api';

const PreferencesModal = ({ isOpen, onClose }) => {
  const [showQuestionnaire, setShowQuestionnaire] = useState(false);
  const [currentPreferences, setCurrentPreferences] = useState(null);

  useEffect(() => {
    if (isOpen) {
      // Load current preferences from backend when modal opens
      loadPreferences();
    }
  }, [isOpen]);

  const loadPreferences = async () => {
    try {
      const response = await preferencesService.getPreferences();
      if (response.data.status === 'success' && response.data.data) {
        setCurrentPreferences(response.data.data);
      } else {
        setCurrentPreferences(null);
      }
    } catch (error) {
      console.error('Error loading preferences:', error);
      setCurrentPreferences(null);
      throw error; // Let the error bubble up
    }
  };

  const handleStartQuestionnaire = () => {
    setShowQuestionnaire(true);
  };

  const handleQuestionnaireComplete = (preferences) => {
    setCurrentPreferences(preferences);
    setShowQuestionnaire(false);
    // Modal will automatically close and dashboard will refresh
    onClose();
  };

  const handleQuestionnaireClose = () => {
    setShowQuestionnaire(false);
  };

  const handleClearPreferences = async () => {
    if (window.confirm('Are you sure you want to clear all your preferences? This action cannot be undone.')) {
      try {
        await preferencesService.deletePreferences();
        setCurrentPreferences(null);
        onClose();
      } catch (error) {
        console.error('Error clearing preferences:', error);
        throw error; // Let the error bubble up instead of falling back
      }
    }
  };

  const formatPreferenceValue = (key, value) => {
    const labels = {
      involvement: {
        'active': "Check in regularly and give feedback",
        'moderate': "Want updates but don't need to micromanage",
        'hands-off': "Prefer to stay out of the way"
      },
      communication: {
        'text-email': "Text or email",
        'phone-face': "Phone calls or face-to-face",
        'flexible': "Flexible with either"
      },
      experience: {
        'experienced': "Several projects",
        'some': "Once or twice",
        'first-time': "First time"
      },
      approach: {
        'detailed': "Detailed quote with everything defined",
        'flexible': "Flexible plan with communication",
        'adaptive': "Figure things out as project moves forward"
      }
    };

    if (key === 'priorities' && Array.isArray(value)) {
      const priorityLabels = {
        'communication': 'Clear and timely communication',
        'schedule': 'Sticking to the schedule',
        'cleanliness': 'Respecting home and keeping clean',
        'pricing': 'Transparent pricing and estimates',
        'independence': 'Taking initiative and working independently',
        'explanation': 'Willingness to explain things clearly'
      };
      return value.map(v => priorityLabels[v] || v).join(', ');
    }

    return labels[key]?.[value] || value;
  };

  if (!isOpen) return null;

  if (showQuestionnaire) {
    return (
      <PersonalityQuestionnaire
        onComplete={handleQuestionnaireComplete}
        onClose={handleQuestionnaireClose}
        initialAnswers={currentPreferences}
      />
    );
  }

  return (
    <div className={styles.overlay}>
      <div className={styles.modal}>
        <div className={styles.header}>
          <h2>
            <i className="fas fa-cog"></i>
            Contractor Preferences
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className={styles.content}>
          {currentPreferences ? (
            <div className={styles.preferencesDisplay}>
              <div className={styles.preferencesHeader}>
                <div className={styles.statusIcon}>
                  <i className="fas fa-check-circle"></i>
                </div>
                <div>
                  <h3>Your Preferences Are Set</h3>
                  <p>These preferences help us match you with contractors that fit your style.</p>
                </div>
              </div>

              <div className={styles.preferencesList}>
                <div className={styles.preferenceItem}>
                  <div className={styles.preferenceLabel}>
                    <i className="fas fa-user-check"></i>
                    Project Involvement
                  </div>
                  <div className={styles.preferenceValue}>
                    {formatPreferenceValue('involvement', currentPreferences.involvement)}
                  </div>
                </div>

                <div className={styles.preferenceItem}>
                  <div className={styles.preferenceLabel}>
                    <i className="fas fa-star"></i>
                    Top Priorities
                  </div>
                  <div className={styles.preferenceValue}>
                    {formatPreferenceValue('priorities', currentPreferences.priorities)}
                  </div>
                </div>

                <div className={styles.preferenceItem}>
                  <div className={styles.preferenceLabel}>
                    <i className="fas fa-comments"></i>
                    Communication Style
                  </div>
                  <div className={styles.preferenceValue}>
                    {formatPreferenceValue('communication', currentPreferences.communication)}
                  </div>
                </div>

                <div className={styles.preferenceItem}>
                  <div className={styles.preferenceLabel}>
                    <i className="fas fa-hammer"></i>
                    Project Experience
                  </div>
                  <div className={styles.preferenceValue}>
                    {formatPreferenceValue('experience', currentPreferences.experience)}
                  </div>
                </div>

                <div className={styles.preferenceItem}>
                  <div className={styles.preferenceLabel}>
                    <i className="fas fa-road"></i>
                    Project Approach
                  </div>
                  <div className={styles.preferenceValue}>
                    {formatPreferenceValue('approach', currentPreferences.approach)}
                  </div>
                </div>
              </div>

              <div className={styles.actions}>
                <button 
                  className={styles.updateButton}
                  onClick={handleStartQuestionnaire}
                >
                  <i className="fas fa-edit"></i>
                  Update Preferences
                </button>
                <button 
                  className={styles.clearButton}
                  onClick={handleClearPreferences}
                >
                  <i className="fas fa-trash-alt"></i>
                  Clear All
                </button>
              </div>
            </div>
          ) : (
            <div className={styles.noPreferences}>
              <div className={styles.emptyIcon}>
                <i className="fas fa-clipboard-question"></i>
              </div>
              <h3>No Preferences Set</h3>
              <p>
                Complete our quick 5-question survey to help us recommend contractors 
                that match your communication style and project preferences.
              </p>
              <button 
                className={styles.startButton}
                onClick={handleStartQuestionnaire}
              >
                <i className="fas fa-play"></i>
                Start Questionnaire
              </button>
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <div className={styles.footerNote}>
            <i className="fas fa-info-circle"></i>
            Your preferences help improve contractor matching and recommendations.
          </div>
        </div>
      </div>
    </div>
  );
};

export default PreferencesModal; 